/* eslint-disable no-underscore-dangle */
import { createAction } from '@reduxjs/toolkit';
import type { ShopState } from '../reducers/shop';
import { paths } from '../schema/docs/api/admin';
import type { AppThunk } from '../store/configureStore';
import { rows, throwOnError } from './utils';

type FetchShopsResponse =
  paths['/api/shops']['get']['responses']['200']['content']['application/json'];
type FetchShopResponse =
  paths['/api/shops/{uuid}']['get']['responses']['200']['content']['application/json'];

export const GET_SHOPS = createAction<ShopState['shops']>('GET_SHOPS');
export const SET_SHOPS_LOADING = createAction('SET_SHOPS_LOADING');
export const CLEAR_SHOPS = createAction<void>('CLEAR_SHOPS');
export const GET_SHOP_ACCOUNTS = createAction<ShopState['shop_accounts']>('GET_SHOP_ACCOUNTS');
export const CLEAR_SHOP_ACCOUNTS = createAction<void>('CLEAR_SHOP_ACCOUNTS');

export const getShops =
  (query = ''): AppThunk =>
  (dispatch) => {
    dispatch(SET_SHOPS_LOADING());

    return fetch(`/api/shops?${query}`, {
      method: 'GET',
      credentials: 'same-origin',
    })
      .then((response) => response.json())
      .then((data) => dispatch(GET_SHOPS(data)));
  };

export const fetchShopsCSV = (query = '') =>
  fetch(`/api/shops/csv?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  }).then(throwOnError<{ url: string }>);

export const clearShops = (): AppThunk => (dispatch) => dispatch(CLEAR_SHOPS());

export const createShop = (postData: Record<string, unknown> = {}) =>
  fetch('/api/shops', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then((response) => response.json());

export const updateShop = (id: string, postData: Record<string, unknown> = {}) =>
  fetch(`/api/shops/${id}`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
  }).then((response) => response.json());

export const showShop = (id: string, query = '') =>
  fetch(`/api/shops/${id}?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
  }).then(throwOnError<FetchShopResponse>);

export const getShopAccounts =
  (id: string, query = ''): AppThunk =>
  (dispatch) =>
    fetch(`/api/shops/${id}/accounts?${query}`, {
      method: 'GET',
      credentials: 'same-origin',
    })
      .then(throwOnError<ShopState['shop_accounts']>)
      .then((data) => dispatch(GET_SHOP_ACCOUNTS(data)))
      .catch(() => dispatch(GET_SHOP_ACCOUNTS([])));

export const clearShopAccounts = (): AppThunk => (dispatch) => dispatch(CLEAR_SHOP_ACCOUNTS());

export const bulkUpdateShops =
  (postData: Record<string, unknown> = {}) =>
  () =>
    fetch('/api/shops/bulk-update', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'content-type': 'application/json',
      },
      body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...postData }),
    });

export const fetchShopAccounts = (id: string, query = '') =>
  fetch(`/api/shops/${id}/accounts?${query}`, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'content-type': 'application/json',
    },
  }).then(throwOnError);

export const fetchShops = (query = '') =>
  fetch(`/api/shops?per_page=10000&${query}`, {
    method: 'GET',
    credentials: 'same-origin',
  })
    .then(throwOnError<FetchShopsResponse>)
    .then(rows);

type ShopInfoForApp =
  paths['/api/shops/{shop_id}/info-for-app']['get']['responses']['200']['content']['application/json'];
type ShopLogoImageForApp =
  paths['/api/shops/{shop_id}/logo-image-for-app']['put']['responses']['200']['content']['application/json'];
type ShopImagesForApp =
  paths['/api/shops/{shop_id}/images-for-app']['post']['responses']['200']['content']['application/json'];

export type ShopInfoForAppPayload = {
  display_on_app: boolean;
  latitude?: number | null;
  longitude?: number | null;
  business_hours?: string;
  regular_closed_days?: string;
  url?: string | null;
  description?: string;
};

export type { ShopInfoForApp, ShopLogoImageForApp, ShopImagesForApp };

export const fetchShopInfoForApp = async (shopId: string): Promise<ShopInfoForApp | null> => {
  const response = await fetch(`/api/shops/${shopId}/info-for-app`, {
    credentials: 'same-origin',
  });
  if (response.status === 404) return null;
  if (!response.ok) throw await response.json();
  return response.json();
};

export const createShopInfoForApp = (
  shopId: string,
  data: ShopInfoForAppPayload
): Promise<ShopInfoForApp> =>
  fetch(`/api/shops/${shopId}/info-for-app`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...data }),
  }).then(throwOnError<ShopInfoForApp>);

export const updateShopInfoForApp = (
  shopId: string,
  data: Partial<ShopInfoForAppPayload>
): Promise<ShopInfoForApp> =>
  fetch(`/api/shops/${shopId}/info-for-app`, {
    method: 'PATCH',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token, ...data }),
  }).then(throwOnError<ShopInfoForApp>);

export const uploadShopLogoImageForApp = (
  shopId: string,
  formData: FormData
): Promise<ShopLogoImageForApp> =>
  fetch(`/api/shops/${shopId}/logo-image-for-app`, {
    method: 'PUT',
    credentials: 'same-origin',
    body: formData,
  }).then(throwOnError<ShopLogoImageForApp>);

export const deleteShopLogoImageForApp = (shopId: string): Promise<void> =>
  fetch(`/api/shops/${shopId}/logo-image-for-app`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
  }).then(throwOnError<void>);

export const uploadShopImageForApp = (
  shopId: string,
  formData: FormData
): Promise<ShopImagesForApp> =>
  fetch(`/api/shops/${shopId}/images-for-app`, {
    method: 'POST',
    credentials: 'same-origin',
    body: formData,
  }).then(throwOnError<ShopImagesForApp>);

export const deleteShopImageForApp = (shopId: string, imageId: number): Promise<void> =>
  fetch(`/api/shops/${shopId}/images-for-app/${imageId}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ _csrf_token: (window as any)._csrf_token }),
  }).then(throwOnError<void>);
